import React, { useState } from 'react';
import {
  Receipt, Download, Eye, Filter, Search, Calendar, DollarSign,
  CheckCircle, Clock, AlertCircle, ChevronDown, X, Package,
  FileText, Truck, Plane, ArrowRight
} from 'lucide-react';

// Mock invoice data
const mockInvoices = [
  {
    id: 'INV-8521',
    date: 'Dec 15, 2025',
    dueDate: 'Dec 30, 2025',
    amount: 12450,
    status: 'pending',
    packages: 3,
    weight: '35.2 lbs',
    manifest: 'MAN-2025-12-15-001',
    items: [
      { description: 'Freight Charge (35.2 lbs)', qty: 1, rate: 7254, amount: 7254 },
      { description: 'Service Contribution (Bags)', qty: 2, rate: 1000, amount: 2000 },
      { description: 'Pallet Fee (Bags)', qty: 2, rate: 1296, amount: 2592 },
      { description: 'Delivery Fee', qty: 1, rate: 1000, amount: 1000 },
    ],
    trackingNumbers: ['BN-45821-JM', 'BN-45822-JM', 'BN-45823-JM']
  },
  {
    id: 'INV-8498',
    date: 'Dec 10, 2025',
    dueDate: 'Dec 25, 2025',
    paidDate: 'Dec 12, 2025',
    amount: 8920,
    status: 'paid',
    packages: 2,
    weight: '22.8 lbs',
    manifest: 'MAN-2025-12-10-002',
    items: [
      { description: 'Freight Charge (22.8 lbs)', qty: 1, rate: 4624, amount: 4624 },
      { description: 'Service Contribution (Bags)', qty: 1, rate: 1000, amount: 1000 },
      { description: 'Service Contribution (Singles)', qty: 1, rate: 1500, amount: 1500 },
      { description: 'Pallet Fee', qty: 1, rate: 796, amount: 796 },
      { description: 'Delivery Fee', qty: 1, rate: 1000, amount: 1000 },
    ],
    trackingNumbers: ['BN-45789-JM', 'BN-45790-JM']
  },
  {
    id: 'INV-8476',
    date: 'Dec 5, 2025',
    dueDate: 'Dec 20, 2025',
    paidDate: 'Dec 8, 2025',
    amount: 15680,
    status: 'paid',
    packages: 4,
    weight: '48.5 lbs',
    manifest: 'MAN-2025-12-05-001',
    items: [
      { description: 'Freight Charge (48.5 lbs)', qty: 1, rate: 9888, amount: 9888 },
      { description: 'Service Contribution (Bags)', qty: 3, rate: 1000, amount: 3000 },
      { description: 'Pallet Fee (Bags)', qty: 3, rate: 1296, amount: 3888 },
      { description: 'Delivery Fee', qty: 1, rate: 1000, amount: 1000 },
    ],
    trackingNumbers: ['BN-45700-JM', 'BN-45701-JM', 'BN-45702-JM', 'BN-45703-JM']
  },
  {
    id: 'INV-8432',
    date: 'Nov 28, 2025',
    dueDate: 'Dec 13, 2025',
    amount: 6840,
    status: 'overdue',
    packages: 1,
    weight: '15.2 lbs',
    manifest: 'MAN-2025-11-28-003',
    items: [
      { description: 'Freight Charge (15.2 lbs)', qty: 1, rate: 3544, amount: 3544 },
      { description: 'Service Contribution (Singles)', qty: 1, rate: 1500, amount: 1500 },
      { description: 'Pallet Fee (Singles)', qty: 1, rate: 648, amount: 648 },
      { description: 'Delivery Fee', qty: 1, rate: 1000, amount: 1000 },
      { description: 'Late Fee', qty: 1, rate: 148, amount: 148 },
    ],
    trackingNumbers: ['BN-45650-JM']
  }
];

const summaryData = {
  pending: { count: 1, amount: 12450 },
  paid: { count: 2, amount: 24600 },
  overdue: { count: 1, amount: 6840 },
};

export default function CustomerInvoices() {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const filteredInvoices = mockInvoices.filter(inv => {
    const matchesFilter = filter === 'all' || inv.status === filter;
    const matchesSearch = inv.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         inv.manifest.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle size={16} />;
      case 'pending': return <Clock size={16} />;
      case 'overdue': return <AlertCircle size={16} />;
      default: return null;
    }
  };

  const openInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDetailModal(true);
  };

  return (
    <div className="customer-invoices-page" data-testid="customer-invoices">
      {/* Summary Cards */}
      <div className="invoice-summary-grid">
        <div className="summary-card pending" data-testid="summary-pending">
          <div className="summary-icon">
            <Clock size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-label">Pending</span>
            <span className="summary-value">JMD {summaryData.pending.amount.toLocaleString()}</span>
            <span className="summary-count">{summaryData.pending.count} invoice(s)</span>
          </div>
        </div>
        
        <div className="summary-card paid" data-testid="summary-paid">
          <div className="summary-icon">
            <CheckCircle size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-label">Paid (This Month)</span>
            <span className="summary-value">JMD {summaryData.paid.amount.toLocaleString()}</span>
            <span className="summary-count">{summaryData.paid.count} invoice(s)</span>
          </div>
        </div>
        
        <div className="summary-card overdue" data-testid="summary-overdue">
          <div className="summary-icon">
            <AlertCircle size={24} />
          </div>
          <div className="summary-content">
            <span className="summary-label">Overdue</span>
            <span className="summary-value">JMD {summaryData.overdue.amount.toLocaleString()}</span>
            <span className="summary-count">{summaryData.overdue.count} invoice(s)</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="invoices-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search invoices or manifests..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="invoice-search"
          />
        </div>
        
        <div className="filter-buttons">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
            data-testid="filter-all"
          >
            All
          </button>
          <button 
            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
            data-testid="filter-pending"
          >
            Pending
          </button>
          <button 
            className={`filter-btn ${filter === 'paid' ? 'active' : ''}`}
            onClick={() => setFilter('paid')}
            data-testid="filter-paid"
          >
            Paid
          </button>
          <button 
            className={`filter-btn ${filter === 'overdue' ? 'active' : ''}`}
            onClick={() => setFilter('overdue')}
            data-testid="filter-overdue"
          >
            Overdue
          </button>
        </div>
      </div>

      {/* Invoice List */}
      <div className="invoices-list">
        {filteredInvoices.map((invoice) => (
          <div 
            key={invoice.id} 
            className={`invoice-card ${invoice.status}`}
            data-testid={`invoice-${invoice.id}`}
          >
            <div className="invoice-card-header">
              <div className="invoice-id-section">
                <Receipt size={20} />
                <div>
                  <span className="invoice-id">{invoice.id}</span>
                  <span className="manifest-ref">Manifest: {invoice.manifest}</span>
                </div>
              </div>
              <div className={`status-badge ${invoice.status}`}>
                {getStatusIcon(invoice.status)}
                <span>{invoice.status}</span>
              </div>
            </div>

            <div className="invoice-card-body">
              <div className="invoice-details">
                <div className="detail-item">
                  <Calendar size={14} />
                  <span>Issued: {invoice.date}</span>
                </div>
                <div className="detail-item">
                  <Clock size={14} />
                  <span>Due: {invoice.dueDate}</span>
                </div>
                <div className="detail-item">
                  <Package size={14} />
                  <span>{invoice.packages} package(s), {invoice.weight}</span>
                </div>
              </div>
              
              <div className="invoice-amount">
                <span className="amount-label">Total</span>
                <span className="amount-value">JMD {invoice.amount.toLocaleString()}</span>
              </div>
            </div>

            <div className="invoice-card-footer">
              <button 
                className="btn btn-secondary"
                onClick={() => openInvoiceDetail(invoice)}
                data-testid={`view-${invoice.id}`}
              >
                <Eye size={14} /> View Details
              </button>
              <button className="btn btn-ghost" data-testid={`download-${invoice.id}`}>
                <Download size={14} /> Download
              </button>
              {invoice.status !== 'paid' && (
                <button className="btn btn-primary" data-testid={`pay-${invoice.id}`}>
                  Pay Now <ArrowRight size={14} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Invoice Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div 
            className="modal-content invoice-detail-modal" 
            onClick={(e) => e.stopPropagation()}
            data-testid="invoice-detail-modal"
          >
            <div className="modal-header">
              <div>
                <h3>Invoice {selectedInvoice.id}</h3>
                <span className="modal-subtitle">Manifest: {selectedInvoice.manifest}</span>
              </div>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* Invoice Header */}
              <div className="invoice-detail-header">
                <div className="detail-section">
                  <h4>Invoice Details</h4>
                  <div className="detail-grid">
                    <div className="detail-row">
                      <span className="label">Invoice Date</span>
                      <span className="value">{selectedInvoice.date}</span>
                    </div>
                    <div className="detail-row">
                      <span className="label">Due Date</span>
                      <span className="value">{selectedInvoice.dueDate}</span>
                    </div>
                    {selectedInvoice.paidDate && (
                      <div className="detail-row">
                        <span className="label">Paid Date</span>
                        <span className="value">{selectedInvoice.paidDate}</span>
                      </div>
                    )}
                    <div className="detail-row">
                      <span className="label">Status</span>
                      <span className={`status-badge ${selectedInvoice.status}`}>
                        {getStatusIcon(selectedInvoice.status)}
                        {selectedInvoice.status}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tracking Numbers */}
              <div className="tracking-section">
                <h4>Package Tracking Numbers</h4>
                <div className="tracking-list">
                  {selectedInvoice.trackingNumbers.map((tracking, i) => (
                    <span key={i} className="tracking-tag">{tracking}</span>
                  ))}
                </div>
              </div>

              {/* Line Items - Transparency Engine */}
              <div className="line-items-section">
                <h4>Charges Breakdown</h4>
                <table className="line-items-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Qty</th>
                      <th>Rate (JMD)</th>
                      <th>Amount (JMD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedInvoice.items.map((item, i) => (
                      <tr key={i}>
                        <td>
                          <div className="item-description">
                            {item.description.includes('Freight') && <Plane size={14} />}
                            {item.description.includes('Service') && <Package size={14} />}
                            {item.description.includes('Pallet') && <FileText size={14} />}
                            {item.description.includes('Delivery') && <Truck size={14} />}
                            {item.description.includes('Late') && <AlertCircle size={14} />}
                            <span>{item.description}</span>
                          </div>
                        </td>
                        <td>{item.qty}</td>
                        <td>{item.rate.toLocaleString()}</td>
                        <td>{item.amount.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="3">Total</td>
                      <td><strong>JMD {selectedInvoice.amount.toLocaleString()}</strong></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Formula Explanation */}
              <div className="formula-note">
                <h4>Freight Calculation Formula</h4>
                <code>Freight (JMD) = (Weight_lb ÷ 2.2) × $2.78 × 165</code>
                <p>Weight: {selectedInvoice.weight}</p>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" data-testid="download-pdf">
                <Download size={14} /> Download PDF
              </button>
              {selectedInvoice.status !== 'paid' && (
                <button className="btn btn-primary" data-testid="pay-modal">
                  Pay JMD {selectedInvoice.amount.toLocaleString()}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
